import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrashAlt, FaArrowLeft, FaChartBar, FaPlus, FaMinus, FaEdit } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)'
];

const AdminBookkeeping = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  
  const [bookkeepingData, setBookkeepingData] = useState({
    title: 'Financial Data',
    additionalTitle: 'Additional Data',
    headers: ['Row Name', 'Column1', 'Column2', 'Column3', 'Column4'],
    rows: [
      { name: 'Row1', Column1: '', Column2: '', Column3: '', Column4: '', include: true },
      { name: 'Row2', Column1: '', Column2: '', Column3: '', Column4: '', include: true },
      { name: 'Row3', Column1: '', Column2: '', Column3: '', Column4: '', include: true }
    ],
    chartData: null
  });

  const filteredRows = bookkeepingData?.rows?.filter(row => row.include) || [];
  const excludedRows = bookkeepingData?.rows?.filter(row => !row.include) || [];
  const dataColumns = bookkeepingData?.headers?.filter(h => h !== 'Row Name') || [];

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin-login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setLoading(false);
            return;
          }
          throw new Error("Failed to fetch bookkeeping data");
        }

        const data = await response.json();
        setBookkeepingData(prev => ({
          ...prev,
          ...data,
          headers: Array.isArray(data.headers) ? data.headers : prev.headers,
          rows: Array.isArray(data.rows) ? data.rows : prev.rows
        }));
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const generateChartData = () => {
    return {
      labels: filteredRows.map(row => row.name),
      datasets: dataColumns.map((header, index) => ({
        label: header,
        data: filteredRows.map(row => parseFloat(row[header]) || 0),
        backgroundColor: COLORS[index % COLORS.length],
        borderColor: COLORS[index % COLORS.length].replace('0.7', '1'),
        borderWidth: 1
      }))
    };
  };

  const handleSave = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const chartData = generateChartData();
      const dataToSave = { ...bookkeepingData, chartData };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) throw new Error("Failed to save bookkeeping data");

      const data = await response.json();
      setBookkeepingData(prev => ({
        ...prev,
        ...data,
        headers: Array.isArray(data.headers) ? data.headers : prev.headers,
        rows: Array.isArray(data.rows) ? data.rows : prev.rows,
        chartData: data.chartData || chartData
      }));
      setEditMode(false);
      setError("");
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCellChange = (rowIndex, colName, value) => {
    const updatedRows = [...bookkeepingData.rows];
    updatedRows[rowIndex][colName] = value;
    setBookkeepingData(prev => ({ ...prev, rows: updatedRows }));
  };

  const handleHeaderChange = (index, value) => {
    const newHeaders = [...bookkeepingData.headers];
    newHeaders[index] = value;
    setBookkeepingData(prev => ({ ...prev, headers: newHeaders }));
  };

  const handleIncludeChange = (rowIndex, checked) => {
    const updatedRows = [...bookkeepingData.rows];
    updatedRows[rowIndex].include = checked;
    setBookkeepingData(prev => ({ ...prev, rows: updatedRows }));
  };

  const addRow = () => {
    const newRowName = `Row${bookkeepingData.rows.length + 1}`;
    const newRow = { name: newRowName, include: true };
    bookkeepingData.headers.forEach(header => { newRow[header] = ''; });
    setBookkeepingData(prev => ({ ...prev, rows: [...prev.rows, newRow] }));
  };

  const removeRow = (rowIndex) => {
    const updatedRows = bookkeepingData.rows.filter((_, index) => index !== rowIndex);
    setBookkeepingData(prev => ({ ...prev, rows: updatedRows }));
  };

  const addColumn = () => {
    const newHeader = `Column${bookkeepingData.headers.length}`;
    const updatedHeaders = [...bookkeepingData.headers, newHeader];
    const updatedRows = bookkeepingData.rows.map(row => ({ ...row, [newHeader]: '' }));
    setBookkeepingData(prev => ({ ...prev, headers: updatedHeaders, rows: updatedRows }));
  };

  const removeColumn = (colIndex) => {
    if (colIndex === 0) return;
    const headerToRemove = bookkeepingData.headers[colIndex];
    const updatedHeaders = bookkeepingData.headers.filter((_, index) => index !== colIndex);
    const updatedRows = bookkeepingData.rows.map(row => {
      const newRow = { ...row };
      delete newRow[headerToRemove];
      return newRow;
    });
    setBookkeepingData(prev => ({ ...prev, headers: updatedHeaders, rows: updatedRows }));
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: bookkeepingData.title, font: { size: 18 } },
      animation: { duration: 2000, easing: 'easeInOutQuad' }
    },
    scales: {
      y: {
        beginAtZero: false,
        ticks: { callback: function(value) { return '$' + value.toLocaleString(); } }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 px-4 py-2 border border-primary-500 text-primary-600 font-medium rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20">
        <FaArrowLeft /> Back to Clients
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Bookkeeping Management</h2>
          <p className="text-gray-500">Client ID: {userId}</p>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700">Save Changes</button>
              <button onClick={() => setEditMode(false)} className="px-4 py-2 bg-gray-500 text-white font-medium rounded-xl hover:bg-gray-600">Cancel</button>
            </>
          ) : (
            <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700">
              <FaEdit /> Edit Data
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl">{error}</div>}

      <div className="mb-6 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-dark-700">
          <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-dark-600 w-fit">
            <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium">Data Table</button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50"><FaChartBar /> Graph Chart</button>
          </div>
        </div>
        <div className="p-4">
          {editMode && (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Main Title</label>
                  <input type="text" value={bookkeepingData.title} onChange={(e) => setBookkeepingData({ ...bookkeepingData, title: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Additional Data Title</label>
                  <input type="text" value={bookkeepingData.additionalTitle} onChange={(e) => setBookkeepingData({ ...bookkeepingData, additionalTitle: e.target.value })} className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={addRow} className="flex items-center gap-1 px-4 py-2 border border-primary-500 text-primary-600 font-medium rounded-xl hover:bg-primary-50"><FaPlus /> Add Row</button>
                <button onClick={addColumn} className="flex items-center gap-1 px-4 py-2 border border-primary-500 text-primary-600 font-medium rounded-xl hover:bg-primary-50"><FaPlus /> Add Column</button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full mb-4">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  {bookkeepingData.headers?.map((header, index) => (
                    <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                      {editMode ? (
                        <div>
                          <input type="text" value={header} onChange={(e) => handleHeaderChange(index, e.target.value)} className="w-full px-2 py-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-gray-900 dark:text-white text-sm mb-1" />
                          {index !== 0 && <button onClick={() => removeColumn(index)} className="p-1 text-red-600 border border-red-300 rounded hover:bg-red-50"><FaMinus size={10} /></button>}
                        </div>
                      ) : header}
                    </th>
                  ))}
                  {editMode && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Include</th>}
                  {editMode && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {bookkeepingData.rows?.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-gray-100 dark:border-dark-700">
                    {bookkeepingData.headers.map((header, colIndex) => (
                      <td key={colIndex} className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {editMode ? (
                          <input type="text" value={row[header] || ''} onChange={(e) => handleCellChange(rowIndex, header, e.target.value)} className="w-full px-2 py-1 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded text-gray-900 dark:text-white text-sm" />
                        ) : row[header]}
                      </td>
                    ))}
                    {editMode && (
                      <>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={row.include} onChange={(e) => handleIncludeChange(rowIndex, e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-primary-600" />
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => removeRow(rowIndex)} className="p-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"><FaTrashAlt /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {bookkeepingData.chartData && (
            <div style={{ height: '400px' }}>
              <Bar data={bookkeepingData.chartData} options={chartOptions} />
            </div>
          )}
        </div>
      </div>

      {excludedRows.length > 0 && (
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-dark-700">
            <h5 className="font-semibold text-gray-900 dark:text-white">{bookkeepingData.additionalTitle}</h5>
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  {bookkeepingData.headers.map((header, index) => (
                    <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {excludedRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-gray-100 dark:border-dark-700">
                    {bookkeepingData.headers.map((header, colIndex) => (
                      <td key={colIndex} className="px-4 py-3 text-gray-700 dark:text-gray-300">{row[header]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookkeeping;